import * as React from 'react';
import { Alert, Card, Empty, Spin, Table, Tag, Typography } from 'antd';
import moment from 'moment-timezone';
import { UserQueries } from '../../../queries/user-queries';
import { TwoFactorDeliveryHistoryItem } from '../../../models/two-factor-delivery-history-interface';

const { Text, Link } = Typography;

const ET_ZONE = 'America/New_York';
const TWILIO_STATUS_URL = 'https://status.twilio.com/';

type Props = {
  userId: number | undefined;
};

const statusColor = (status: string): string => {
  switch (status) {
    case 'Fail':
      return 'red';
    case 'Partial':
      return 'orange';
    default:
      return 'green';
  }
};

const TwoFactorDeliveryHistoryCard: React.FC<Props> = ({ userId }) => {
  const historyQuery = UserQueries.useTwoFactorDeliveryHistory(userId);

  const columns = [
    {
      title: 'Delivery Date & Time',
      dataIndex: 'deliveryDate',
      key: 'deliveryDate',
      render: (value: string | null) =>
        value ? moment.utc(value).tz(ET_ZONE).format('MM/DD/YYYY hh:mm A') : '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value: string) => <Tag color={statusColor(value)}>{value}</Tag>,
    },
    {
      title: 'Error (if applicable)',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      render: (value: string | null) => value || '—',
    },
  ];

  return (
    <Card
      className='user-detail-card'
      title={<h4 className='primary-text'><strong>Two Factor Code Delivery History (Past 7 Days)</strong></h4>}
      bordered={false}
    >
      <Spin spinning={historyQuery.isLoading}>
        {historyQuery.isError ? (
          <Alert
            type='error'
            message='Unable to load delivery history'
            description='The request to retrieve delivery history failed. Try refreshing the page.'
            showIcon
          />
        ) : (
          <Table<TwoFactorDeliveryHistoryItem>
            dataSource={historyQuery.data ?? []}
            columns={columns}
            rowKey={(_, index) => String(index)}
            pagination={false}
            size='small'
            locale={{ emptyText: <Empty description='No delivery history' image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          />
        )}
        <div style={{ marginTop: 12 }}>
          <Text type='secondary'>All times shown in Eastern Time (ET)</Text>
        </div>
        <div style={{ marginTop: 8 }}>
          <Link href={TWILIO_STATUS_URL} target='_blank' rel='noopener noreferrer'>
            Check Twilio status page
          </Link>
        </div>
      </Spin>
    </Card>
  );
};

export default TwoFactorDeliveryHistoryCard;
